import React, {Component} from "react";
import {Control, Field, Icon, Label, Select} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import InformationCard from "../../../elements/InformationCard";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {getImageContent} from "../../../api/models/ImageContent";
import OCRLanguages from "../../../components/model/ImageContent/OCRLanguages";

interface IImageContentModelState {
    loading: boolean;
    selectedOption: string;
    languages: any;
    languageCode: string;
    emptyOption: boolean;
    file: any,
    imageUrl: any,
    imageBase64: any,
    fileErrorMessage: string,
    result: any;
    errorMessage: string;
}

class ImageContentModel extends Component<{}, IImageContentModelState> {
    public ALLOWED_FILE_TYPES: any = ['image/jpeg', 'image/png'];
    public ALLOWED_FILE_TYPES_STR: string = this.ALLOWED_FILE_TYPES.join(',');
    public fileInputRef: any = null;
    public OPTION_OCR = 'content-mod-ocr';
    public OPTION_EVALUATE = 'content-mod-evaluate';

    public options: any = [{
        id: '',
        name: '-'
    }, {
        id: this.OPTION_OCR,
        name: 'Extract Text (OCR)'
    }, {
        id: this.OPTION_EVALUATE,
        name: 'Detect Unwanted Content'
    }];

    public fileInputStyle = {
        width: '100%'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            selectedOption: "",
            languages: [],
            languageCode: "eng",
            emptyOption: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: "",
            errorMessage: ""
        };
    }

    public async componentDidMount() {
        const languages = await OCRLanguages.list();
        if (languages) {
            this.setState({
                languages
            });
        }
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.IMAGE_CONTENT);
    }

    public handleFileChange = (e: any) => {
        this.setState({
            fileErrorMessage: ""
        });

        const file = e.target.files[0];
        if (file) {
            if (this.ALLOWED_FILE_TYPES.includes(file.type)) {

                const url = URL.createObjectURL(file);
                this.setState({
                    file,
                    imageUrl: url
                }, () => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        this.setState({
                            imageBase64: reader.result
                        });
                    };
                    reader.readAsDataURL(file);
                });
            } else {
                this.setState({
                    file: null,
                    imageUrl: "",
                    imageBase64: "",
                    fileErrorMessage: "The model only accepts JPEG or PNG format files."
                });
            }
        }
    };

    public handleOptionChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            selectedOption: value,
            emptyOption: false,
            result: "",
            languageCode: "eng"
        });
    };

    public handleLanguageChange = (e: any) => {
        this.setState({
            languageCode: e.target.value
        });
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        let valid = true;

        if (this.state.loading) {
            valid = false;
        }

        if (!this.state.file || !this.state.imageBase64 || this.state.fileErrorMessage || !this.state.selectedOption) {
            if (!this.state.file || !this.state.imageBase64) {
                this.setState({
                    fileErrorMessage: "Please select image."
                });
            }

            if (!this.state.selectedOption) {
                this.setState({
                    emptyOption: true
                });
            }

            valid = false;
        }

        if (!valid) {
            this.setState({result: ""});
            return;
        }

        this.setState({
            loading: true,
            result: "",
            errorMessage: ""
        });

        let errorOccurred = false;

        const formData = new FormData();
        formData.append("image", this.state.file);
        formData.append("function", this.state.selectedOption);
        if (this.state.selectedOption === this.OPTION_OCR) {
            formData.append("language", this.state.languageCode);
        }

        const response = await getImageContent(formData).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process image."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            this.setState({loading: false});
            return;
        }

        if (response && response.data) {
            const responseData = response.data;
            if (this.state.selectedOption === this.OPTION_OCR) {
                if (responseData.text) {
                    this.setState({
                        loading: false,
                        result: response.data.text
                    });
                } else {
                    this.setState({
                        loading: false,
                        errorMessage: "Couldn't process image."
                    });
                }
            } else if (this.state.selectedOption === this.OPTION_EVALUATE) {
                const result: any = [];

                if (responseData.is_image_adult_classified) {
                    result.push({
                        text: "Adult content detected.",
                        iconClass: "fas fa-exclamation-circle",
                        color: "red"
                    });
                }
                if (responseData.is_image_racy_classified) {
                    result.push({
                        text: "Racy content detected.",
                        iconClass: "fas fa-exclamation-circle",
                        color: "red"
                    });
                }
                if (!result.length) {
                    result.push({
                        text: "Unwanted content not detected.",
                        iconClass: "fas fa-check",
                        color: "green"
                    });
                }

                this.setState({
                    loading: false,
                    result
                });
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process image."
                });
            }
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process image."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.IMAGE_CONTENT);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            selectedOption: "",
            languageCode: "eng",
            emptyOption: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: "",
            errorMessage: "",
        });

        if (this.fileInputRef) {
            this.fileInputRef.value = "";
        }
    };

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                <SectionName>Select Image:</SectionName>
                                <input
                                    ref={(el) => this.fileInputRef = el}
                                    type="file"
                                    accept={this.ALLOWED_FILE_TYPES_STR}
                                    onChange={this.handleFileChange}
                                    style={this.fileInputStyle}
                                />
                                {
                                    this.state.fileErrorMessage ? <div>
                                        <InputWarning>{this.state.fileErrorMessage}</InputWarning>
                                    </div> : <></>
                                }
                                {
                                    !this.state.fileErrorMessage && this.state.file && this.state.imageUrl ?
                                        <div style={{
                                            margin: '20px',
                                            textAlign: 'center',
                                            position: 'relative'
                                        }}>
                                            <img
                                                alt="Image"
                                                src={this.state.imageUrl}
                                            />
                                        </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <Field>
                            <Label>Select Option:</Label>
                            <Control>
                                <Select
                                    value={this.state.selectedOption}
                                    onChange={this.handleOptionChange}
                                >
                                    {
                                        this.options.map((option: any) => {
                                            return <option key={option.id} value={option.id}>{option.name}</option>;
                                        })
                                    }
                                </Select>
                                {
                                    this.state.emptyOption ? <div>
                                        <InputWarning>Please select option.</InputWarning>
                                    </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        {
                            this.state.selectedOption === this.OPTION_OCR ? <React.Fragment>
                                <Field>
                                    <Label>Language:</Label>
                                    <Control>
                                        <Select
                                            value={this.state.languageCode}
                                            onChange={this.handleLanguageChange}
                                        >
                                            {
                                                this.state.languages.map((lang: any) => {
                                                    return <option key={lang.code}
                                                                   value={lang.code}>{lang.name}</option>;
                                                })
                                            }
                                        </Select>
                                    </Control>
                                </Field>
                                <Spacer/>
                            </React.Fragment> : <></>
                        }
                        <div>
                            <Button onClick={this.onSubmit}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        {
                            this.state.loading && <div>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.result && this.state.selectedOption === this.OPTION_OCR ?
                                <div>
                                    <SectionName>Output:</SectionName>
                                    <InformationCard>
                                        {this.state.result}
                                    </InformationCard>
                                </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.result && this.state.selectedOption === this.OPTION_EVALUATE ?
                                <div>
                                    <SectionName>Output:</SectionName>
                                    <InformationCard>
                                        {
                                            this.state.result.map((item: any, index: number) => {
                                                return <div key={index}>
                                                <span style={{margin: '0 5px 0 10px'}}>
                                                    <Icon className={item.iconClass} style={{color: item.color}}/>
                                                </span>
                                                    <span>{item.text}</span>
                                                </div>;
                                            })
                                        }
                                    </InformationCard>
                                </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.errorMessage ? <div>
                                {this.state.errorMessage}
                            </div> : <></>
                        }
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default ImageContentModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/ImageContent/ImageContentModel.tsx